/**
 * Bob Laadt Op — SiteHeader.
 * Gedrag 1-op-1 uit het ontwerp (index.html, JS 3248–3349):
 *   - .is-solid op de header zodra je voorbij 30px scrolt (promobalk vouwt dan weg)
 *   - mobiel menu (hamburger), sluit na klik op een link
 *   - uitklapmenu: klik op het chevron (touch), Escape en klikken-buiten sluiten
 *   - het zwevende label bij de nav-CTA volgt de cursor
 * Scrollpositie via window.bobScroll (bob-base.js): Horizon laat vanaf 990px
 * `.page-wrapper` scrollen i.p.v. het venster.
 * Teksten (aria-labels) komen via data-attributen uit Liquid.
 */

/** Drempel uit het ontwerp: `scrollY > 30`. */
const SOLID_AFTER = 30;

/** Terugval voor het geval bob-base.js (nog) niet geladen is. */
const scroller = {
  get y() {
    if (window.bobScroll) return window.bobScroll.y;
    const wrapper = document.querySelector('.page-wrapper');
    return Math.max(window.scrollY || 0, wrapper ? wrapper.scrollTop : 0);
  },
  /** @param {EventListener} fn */
  on(fn) {
    if (window.bobScroll) window.bobScroll.on(fn);
    else document.addEventListener('scroll', fn, { passive: true, capture: true });
  },
  /** @param {EventListener} fn */
  off(fn) {
    if (window.bobScroll) window.bobScroll.off(fn);
    else document.removeEventListener('scroll', fn, { capture: true });
  },
};

class BobHeader extends HTMLElement {
  connectedCallback() {
    this.header = this.querySelector('.site-header');
    this.navToggle = this.querySelector('.nav-toggle');
    this.mobileMenu = this.querySelector('.mobile-menu');
    this.navItems = Array.from(this.querySelectorAll('.nav-item'));
    this.navCta = this.querySelector('.nav-cta');
    this.ctaTag = this.navCta ? this.navCta.querySelector('.cta-tag') : null;
    this.headerTick = false;

    scroller.on(this.onScroll);
    document.addEventListener('keydown', this.onKeydown);
    document.addEventListener('click', this.onDocumentClick);

    this.navToggle?.addEventListener('click', this.onToggleClick);
    this.mobileMenu?.addEventListener('click', this.onMobileMenuClick);

    this.navItems.forEach((item) => {
      item.querySelector('.nav-sub-toggle')?.addEventListener('click', this.onSubToggleClick);
    });

    if (this.navCta && this.ctaTag) {
      this.navCta.addEventListener('pointerenter', this.onCtaEnter);
      this.navCta.addEventListener('pointermove', this.placeTag);
    }

    this.syncHeader();
  }

  disconnectedCallback() {
    scroller.off(this.onScroll);
    document.removeEventListener('keydown', this.onKeydown);
    document.removeEventListener('click', this.onDocumentClick);
  }

  /* ── Header: transparant boven de hero, solid zodra je scrolt ─────── */
  syncHeader = () => {
    this.header?.classList.toggle('is-solid', scroller.y > SOLID_AFTER);
  };

  onScroll = () => {
    if (this.headerTick) return;
    this.headerTick = true;
    requestAnimationFrame(() => {
      this.syncHeader();
      this.headerTick = false;
    });
  };

  /* ── Mobiel menu (primaire CTA blijft buiten het menu zichtbaar) ──── */
  onToggleClick = () => {
    if (!this.mobileMenu || !this.navToggle) return;
    const open = this.mobileMenu.classList.toggle('is-open');
    this.navToggle.setAttribute('aria-expanded', String(open));
    const label = open ? this.dataset.labelClose : this.dataset.labelOpen;
    if (label) this.navToggle.setAttribute('aria-label', label);
    this.syncHeader();
  };

  /** @param {MouseEvent} event */
  onMobileMenuClick = (event) => {
    if (!(event.target instanceof Element) || !event.target.closest('a')) return;
    this.mobileMenu?.classList.remove('is-open');
    this.navToggle?.setAttribute('aria-expanded', 'false');
    if (this.dataset.labelOpen) this.navToggle?.setAttribute('aria-label', this.dataset.labelOpen);
    this.syncHeader();
  };

  /* ── Uitklapmenu ──────────────────────────────────────────────────────
     Hover en toetsenbordfocus doet de CSS. Dit is er voor de klik op het
     chevron (nodig op touch) plus Escape en klikken-buiten. */
  /** @param {Element} [except] */
  closeSubs(except) {
    this.navItems.forEach((item) => {
      if (item === except) return;
      item.classList.remove('is-open');
      item.querySelector('.nav-sub-toggle')?.setAttribute('aria-expanded', 'false');
    });
  }

  /** @param {MouseEvent} event */
  onSubToggleClick = (event) => {
    const button = /** @type {HTMLElement} */ (event.currentTarget);
    const item = button.closest('.nav-item');
    if (!item) return;
    const open = !item.classList.contains('is-open');
    this.closeSubs(item);
    item.classList.toggle('is-open', open);
    button.setAttribute('aria-expanded', String(open));
  };

  /** @param {KeyboardEvent} event */
  onKeydown = (event) => {
    if (event.key !== 'Escape') return;
    const open = this.navItems.find((item) => item.classList.contains('is-open'));
    if (!open) return;
    /** @type {HTMLElement | null} */ (open.querySelector('.nav-sub-toggle'))?.focus(); // focus niet kwijtraken
    this.closeSubs();
  };

  /** @param {MouseEvent} event */
  onDocumentClick = (event) => {
    if (!(event.target instanceof Element) || !event.target.closest('.nav-item')) this.closeSubs();
  };

  /* ── Hover-tag bij de nav-CTA volgt de cursor ─────────────────────── */
  /** @param {PointerEvent} event */
  placeTag = (event) => {
    if (!this.navCta || !this.ctaTag) return;
    const r = this.navCta.getBoundingClientRect();
    // Rechts begrenzen zodat het label niet buiten het scherm valt
    const maxX = window.innerWidth - 14 - this.ctaTag.offsetWidth;
    const x = Math.min(event.clientX + 16, maxX);
    this.ctaTag.style.setProperty('--tx', `${x - r.left}px`);
    this.ctaTag.style.setProperty('--ty', `${event.clientY - r.top + 18}px`);
  };

  /** @param {PointerEvent} event */
  onCtaEnter = (event) => {
    if (!this.ctaTag) return;
    // Verschijn precies waar de cursor binnenkomt: even zonder overgang
    // plaatsen, anders schuift het label vanaf de vorige positie mee.
    this.ctaTag.style.transition = 'none';
    this.placeTag(event);
    void this.ctaTag.offsetWidth;
    this.ctaTag.style.transition = '';
  };
}

if (!customElements.get('bob-header')) customElements.define('bob-header', BobHeader);
